import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
const getRoute = (item: {
  href?: string;
  label: string;
}) => {
  if (item.href && item.href !== "#") {
    return item.href.startsWith("/") ? item.href : `/${item.href}`;
  }
  const fallback = item.label.replace(/\s+/g, "");
  return `/${fallback}`;
};
function DesktopNavigation() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="12037" className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted animate-pulse rounded-lg" />)}
      </div>;
  }
  const reorderedItems = [...menuItems];
  const caregiverIdx = reorderedItems.findIndex(item => item.label === "Caregiver Corner");
  const cliniciansIdx = reorderedItems.findIndex(item => item.label?.includes("Clinicians and Child Life Specialists"));
  if (caregiverIdx !== -1 && cliniciansIdx !== -1) {
    const [itemToMove] = reorderedItems.splice(cliniciansIdx, 1);
    const targetIdx = reorderedItems.findIndex(item => item.label === "Caregiver Corner");
    reorderedItems.splice(targetIdx + 1, 0, itemToMove);
  }
  return <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-6">
        {reorderedItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={getRoute(item)} data-menu-item={item.label} className="text-sm font-medium text-foreground transition-all hover:underline hover:decoration-2 hover:decoration-secondary hover:underline-offset-8">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavigation({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  const reorderedItems = [...menuItems];
  if (!loading) {
    const caregiverIdx = reorderedItems.findIndex(item => item.label === "Caregiver Corner");
    const cliniciansIdx = reorderedItems.findIndex(item => item.label?.includes("Clinicians and Child Life Specialists"));
    if (caregiverIdx !== -1 && cliniciansIdx !== -1) {
      const [itemToMove] = reorderedItems.splice(cliniciansIdx, 1);
      const targetIdx = reorderedItems.findIndex(item => item.label === "Caregiver Corner");
      reorderedItems.splice(targetIdx + 1, 0, itemToMove);
    }
  }
  return <div className="fixed inset-0 top-[60px] z-40 bg-background/95 backdrop-blur-xl border-t border-border md:hidden flex flex-col p-6 animate-in slide-in-from-top-5 fade-in duration-200">
      <nav className="flex flex-col gap-6">
        {loading ? <div className="flex flex-col gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-lg" />)}
          </div> : reorderedItems.map((item, i) => <Link key={item.id} to={getRoute(item)} data-menu-item={item.label} className="text-lg font-medium text-foreground py-2 border-b border-border/30" onClick={() => setIsOpen(false)} data-index={i}>
              {item.label}
            </Link>)}
        <Button asChild className="w-full mt-4 bg-[hsl(43,89%,55%)] hover:bg-[hsl(43,89%,45%)] text-white rounded-lg shadow-lg shadow-yellow-500/10" size="lg">
          <Link to="/DonorPartnerHubPage" onClick={() => setIsOpen(false)}>
            Partner With Us
          </Link>
        </Button>
      </nav>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="main-navigation" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b", isScrolled ? "bg-background/80 backdrop-blur-xl border-yellow-500/10 shadow-sm" : "bg-background border-transparent")}>
      <div className="container mx-auto px-4 h-[70px] flex items-center justify-between relative">
        
        <div className="md:hidden flex items-center z-50">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" className="text-foreground">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>

        
        <div className="absolute left-1/2 -translate-x-1/2 md:static md:translate-x-0 md:mr-8 flex-shrink-0">
          <Link to="/HomePage" className="flex items-center">
            <img src="/img/the_story_haven_logo_header.png" alt="The Story Haven Logo" className="h-10" />
          </Link>
        </div>

        
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="45">
            <DesktopNavigation />
          </MenuProvider>
        </div>

        
        <div className="flex items-center justify-end gap-4 md:min-w-[160px]">
          <Button asChild size="sm" className="hidden md:inline-flex font-medium bg-[hsl(43,89%,55%)] hover:bg-[hsl(43,89%,45%)] text-white rounded-lg shadow-md shadow-yellow-500/10 px-6">
            <Link to="/DonorPartnerHubPage">Partner With Us</Link>
          </Button>
          
          <Button asChild size="sm" className="md:hidden text-xs px-4 h-8 bg-[hsl(43,89%,55%)] hover:bg-[hsl(43,89%,45%)] text-white rounded-lg">
            <Link to="/DonorPartnerHubPage">Donate</Link>
          </Button>
        </div>
      </div>

      
      <MenuProvider menu_id="45">
        <MobileNavigation isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
      </MenuProvider>
    </header>;
}